import {
  Toolbar,
  Typography,
  Button,
  Box,
  Tooltip,
  Chip,
  Stack,
  ButtonGroup
} from '@mui/material'
import {
  DeleteOutlineRounded,
  AddCircleRounded,
  TuneOutlined
} from '@mui/icons-material'

import { useEffect, useRef, useState } from 'react'
import AlertUI from './AlertUI'
import { SmartDeleteDialog } from './utilities/SmartDialog'
import SmartSearch from './formComponent/Search'
import ImportFile from '../importFile'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import NonDeletableItemsDataShowAlert from './NonDeletableItems/NonDeletableItemsDataShowAlert'
import DownloadIcon from '@mui/icons-material/Download'
import CircularProgressWithLabel from './CircularProgressWithLabel'
import { apiGet } from './Api_Functions'
import downloadAsCsv from '../planner/csv_utils'
import { useDateFormatter } from './hooks/useDateFormatter'
import { serializeCsvArrayCell, walkCsvField } from './csvExportSerializer'

const getTitleStyle = (compactHeader) => ({
  fontSize: compactHeader ? '18px !important' : '25px !important',
  fontWeight: compactHeader ? 'bold' : undefined,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
})

const SmartMaterialUIToolbar = (props) => {
  const {
    multipleSelectionValue,
    title,
    setFilterText,
    filterText,
    actions,
    columns,
    editIndex,
    messageToolbarMultipleSelection,
    setMultipleSelectionValue,
    searchAndPaginationVisible,
    toolbarDisplay,
    smartTableIndexKey,
    toolbar, // { render: { fetchFunction } Left Position, renderSticky: { fetchFunction } Right Position}
    multipleSelectionActions,
    deleteMultipleSelectionDisabled,
    setLoadTable,
    loadTable,
    setDisableRowActions,
    onAddElementClick,
    resetPageCount,
    datafetch,
    pagination,
    pageable,
    importFile,
    renderingFilters,
    importFileDisabled,
    toolBarStyle,
    safeDeleteShowProperty = null,
    allFetchedItemsSet,
    tuneTable = false,
    openTuneTable = () => {},
    requestPagination,
    originalDataFetch,
    hideExport = false,
    compactHeader = false,
    searchPosition = 'right'
  } = props

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleteResponse, setDeleteResponse] = useState(null)
  const [allSelected, setAllSelected] = useState(false)
  const [AlertElement, showAlert] = AlertUI()
  const toolbarStyleCalculated = {
    ...(multipleSelectionValue.rows.length === 0 && !multipleSelectionValue.allSelected
      ? {
          flexGrow: 1,
          height: compactHeader ? 48 : 100,
          minHeight: compactHeader ? 48 : 100
        }
      : {
          flexGrow: 1,
          height: compactHeader ? 48 : 100,
          minHeight: compactHeader ? 48 : 100,
          backgroundColor: theme => theme.palette.background.toolbarMultipleSelection,
          border: theme => `10px  ${theme.palette.primary.main}`,
          borderStyle: 'none none none solid'
        }),
    ...toolBarStyle
  }

  const { t_ } = useSmartTranslation()
  const [downloadingFile, setDownloadingFile] = useState(false)
  const [progress, setProgress] = useState(0)
  const { formatDateTime } = useDateFormatter()
  const toolbarRef = useRef(null)
  const rightToolbarRef = useRef(null)
  const downloadingResetTimeoutRef = useRef()
  const [titleMaxWidth, setTitleMaxWidth] = useState(undefined)

  // Clear the deferred downloading-state reset scheduled after a CSV export, so the
  // timer never outlives the component (#2822).
  useEffect(() => {
    return () => {
      clearTimeout(downloadingResetTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    const updateTitleMaxWidth = () => {
      const toolbarElement = toolbarRef.current
      const rightToolbarElement = rightToolbarRef.current

      if (!toolbarElement || !rightToolbarElement) {
        setTitleMaxWidth(undefined)
        return
      }

      const toolbarRect = toolbarElement.getBoundingClientRect()
      const rightToolbarRect = rightToolbarElement.getBoundingClientRect()
      const toolbarStyle = window.getComputedStyle(toolbarElement)
      const paddingLeft = parseFloat(toolbarStyle.paddingLeft) || 0
      const gap = 8
      const nextWidth = Math.max(0, Math.floor(rightToolbarRect.left - toolbarRect.left - paddingLeft - gap))

      setTitleMaxWidth(previousWidth => previousWidth === nextWidth ? previousWidth : nextWidth)
    }

    updateTitleMaxWidth()

    if (typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined') {
      const resizeObserver = new window.ResizeObserver(updateTitleMaxWidth)
      if (toolbarRef.current) resizeObserver.observe(toolbarRef.current)
      if (rightToolbarRef.current) resizeObserver.observe(rightToolbarRef.current)
      return () => resizeObserver.disconnect()
    }

    window.addEventListener('resize', updateTitleMaxWidth)
    return () => window.removeEventListener('resize', updateTitleMaxWidth)
  }, [actions, hideExport, importFile, searchPosition, toolbar, tuneTable])

  const handleDownload = async () => {
    try {
      let allContent = []
      if (pageable) {
        const requestPaginationCopy = structuredClone(requestPagination)
        setProgress(0)
        setDownloadingFile(true)

        const pageSize = 100
        let page = 0
        let totalPages = null
        requestPaginationCopy.size = pageSize

        while (totalPages === null || page < totalPages) {
          let response
          requestPaginationCopy.page = page
          if (typeof originalDataFetch === 'string') {
            response = await apiGet(originalDataFetch, requestPaginationCopy)
          } else {
            response = await originalDataFetch(requestPaginationCopy)
          }
          if (totalPages === null) {
            totalPages = Math.ceil(response.totalElements / pageSize)
          }
          allContent = allContent.concat(response.content)
          page++
          setProgress(Math.floor((page / totalPages) * 100))
        }
      } else {
        const type = typeof originalDataFetch
        if (type === 'string') {
          allContent = await apiGet(originalDataFetch)
        } else if (type === 'object') {
          allContent = originalDataFetch
        } else {
          allContent = await originalDataFetch()
        }
      }
      const csvSeparator = await apiGet('settings/character').then((result) => {
        if (result[0].length !== 0) {
          return result[0]
        }
      })

      const filteredColumns = columns.filter(col => col.field !== undefined && col.field !== null && col.field !== '')
      const data = allContent.map(line => {
        return filteredColumns.map(column => {
          // Opt-in: a column whose exported value is not its `field` walked on the row declares
          // `csvValue(row)` (e.g. planner details' "Required date", which keeps its historical field for
          // the saved tunes but exports another value of the row). Every other column is unaffected.
          if (typeof column.csvValue === 'function') {
            const value = column.csvValue(line)
            return value === null || value === undefined ? '' : value
          }
          const obj = walkCsvField(line, column.field)
          if (obj == null || obj === '') {
            return ''
          }

          if (Array.isArray(obj)) {
            return serializeCsvArrayCell(obj, csvSeparator)
          }

          if (column.type === 'datetime' || column.type === 'dateTimePicker' || column.type === 'datePicker' || column.type === 'time') {
            return formatDateTime(obj)
          }
          return obj
        })
      })
      const name = title || 'temp'
      downloadAsCsv(filteredColumns, data, name, csvSeparator)
    } catch (error) {
      console.error('Error downloading CSV:', error)
    }
    setProgress(100)
    clearTimeout(downloadingResetTimeoutRef.current)
    downloadingResetTimeoutRef.current = setTimeout(() => setDownloadingFile(false), 600)
  }

  const deleteMultipleSelection = () => {
    // A table can offer multi-selection without a delete action (the host handles the checked rows
    // itself): then there is no bulk-delete button here.
    if (!actions?.delete) return null
    return (
      <Tooltip arrow title='delete selections'>
        <Button
          variant='outlined'
          sx={{ borderColor: 'transparent' }}
          color='error'
          disabled={actions.delete.disabled}
          onClick={() => {
            if (
              typeof actions.delete
                .fetchFunction === 'function'
            ) {
              if (setDisableRowActions !== undefined) setDisableRowActions(true)
              setOpenDeleteDialog(true)
            }
          }}
        >
          <DeleteOutlineRounded sx={{ marginRight: '0.5em' }} color={actions.delete.disabled ? '' : 'error'} />
          <Typography color={actions.delete.disabled ? '' : 'error'}>{t_('DELETE')}</Typography>
        </Button>
      </Tooltip>
    )
  }

  const resetMultiSelectionValue = () => {
    setMultipleSelectionValue({
      rows: [],
      allSelected: false
    })
  }

  const cancelOperation = () => {
    if (setDisableRowActions !== undefined) setDisableRowActions(false)
    showAlert({
      title: t_('Operation cancelled'),
      severity: 'info'
    })
  }

  const deleteFunction = async (multipleSelectionValue) => {
    if (setDisableRowActions !== undefined) setDisableRowActions(false)
    if (safeDeleteShowProperty !== null) {
      const response = await actions.delete.fetchFunction(multipleSelectionValue)
      setAllSelected(multipleSelectionValue.allSelected)
      setDeleteResponse(response)
      resetMultiSelectionValue()
      resetPageCount()
      return
    }
    await actions.delete.fetchFunction(multipleSelectionValue)
      .then((paramsMulti) => {
        if (paramsMulti && paramsMulti.disableAlert) {
          return
        }
        resetMultiSelectionValue()
        resetPageCount()
        showAlert({
          title: t_('Deleted correctly.'),
          severity: 'success'
        })
      })
      .catch((error) => {
        resetMultiSelectionValue()
        showAlert({
          title: '',
          message: error.message,
          severity: error.name
        })
      })
  }

  const getPageableDeleteDialog = () => {
    let deletePageable = null
    if (pageable) {
      deletePageable = datafetch
      deletePageable = { ...pagination }
      deletePageable.size = -1
      deletePageable.page = 0
    }
    return deletePageable
  }

  return (
    <>
      {AlertElement}
      <NonDeletableItemsDataShowAlert
        deleteResponse={deleteResponse}
        allItems={allFetchedItemsSet}
        property={safeDeleteShowProperty}
        allSelected={allSelected}
      />
      <Toolbar ref={toolbarRef} sx={{ ...toolbarStyleCalculated, display: [searchAndPaginationVisible || toolbarDisplay], alignItems: 'center' }}>
        {multipleSelectionValue.rows.length === 0 && !multipleSelectionValue.allSelected
          ? (
            <>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{
                  position: 'sticky',
                  left: 24,
                  zIndex: 1,
                  flex: '0 1 auto',
                  maxWidth: titleMaxWidth !== undefined ? `${titleMaxWidth}px` : undefined,
                  minWidth: 0,
                  overflow: 'hidden',
                  justifyContent: 'flex-start'
                }}
              >
                {typeof title === 'string'
                  ? <Typography title={title} sx={{ ...getTitleStyle(compactHeader), flex: '1 1 auto', maxWidth: '100%' }}>{title}</Typography>
                  : <Box sx={{ flex: '1 1 auto', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>{title}</Box>}
              </Stack>
              <Box sx={{ flexGrow: 1 }} />
              <Stack ref={rightToolbarRef} sx={{ position: 'sticky', right: 24, zIndex: 2, flex: '0 0 auto', justifyContent: 'flex-end' }} direction='row' alignItems='center' spacing={1}>
                {searchPosition === 'left' && <SmartSearch setFilterText={setFilterText} setLoadTable={setLoadTable} loadTable={loadTable} defaultFilterText={filterText} />}
                {toolbar?.render?.fetchFunction ? toolbar.render.fetchFunction({ showAlert }) : null}
                {renderingFilters ? renderingFilters() : null}
                {tuneTable && (
                  <Button
                    sx={{ borderColor: 'transparent' }}
                    color='primary'
                    size='small'
                    variant='outlined'
                    onClick={() => openTuneTable()}
                  >
                    <TuneOutlined />
                    <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('TUNE')}</Typography>
                  </Button>
                )}
                {importFile && <ImportFile disabled={importFileDisabled} beanParams={importFile} columns={columns} />}
                {toolbar?.renderSticky?.fetchFunction ? toolbar.renderSticky.fetchFunction({ showAlert }) : null}

                {actions?.create && (actions.create.render === undefined
                  ? (
                    <Button
                      sx={{ borderColor: 'transparent' }}
                      color='primary'
                      variant='outlined'
                      onClick={() => onAddElementClick?.()}
                      disabled={smartTableIndexKey !== null ? editIndex > -2 || actions.create.disabled : true}
                    >
                      <AddCircleRounded sx={{ fontSize: '1.2em' }} />
                      <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{actions.create?.label ? t_(actions.create.label) : t_('ADD RECORD')}</Typography>
                    </Button>
                    )
                  : typeof actions.create.render === 'function' ? actions.create.render({ handleShowAlert: showAlert }) : null
                )}
                {!hideExport && (
                  <Button
                    sx={{ borderColor: 'transparent' }}
                    color='primary'
                    variant='outlined'
                    endIcon={<DownloadIcon />}
                    onClick={() => handleDownload()}
                    disabled={downloadingFile}
                  >
                    {downloadingFile ? <CircularProgressWithLabel value={progress} /> : t_('Export')}
                  </Button>
                )}
                {searchPosition === 'right' && <SmartSearch setFilterText={setFilterText} setLoadTable={setLoadTable} loadTable={loadTable} defaultFilterText={filterText} />}
              </Stack>
            </>
            )
          : (
            <>
              {multipleSelectionActions && deleteMultipleSelectionDisabled
                ? (
                  <ButtonGroup variant='outlined'>
                    {multipleSelectionActions.map((action, index) => (
                      action.render(datafetch, index, pagination, multipleSelectionValue)
                    ))}
                  </ButtonGroup>
                  )
                : multipleSelectionActions && !deleteMultipleSelectionDisabled
                  ? (
                    <Stack direction='row' gap={1}>
                      {deleteMultipleSelection()}
                      <ButtonGroup variant='outlined'>
                        {multipleSelectionActions.map((action, index) => (
                          action.render(datafetch, index, pagination, multipleSelectionValue)
                        ))}
                      </ButtonGroup>
                    </Stack>
                    )
                  : deleteMultipleSelection()}
              <Box sx={{ flexGrow: 1 }} />
              <Typography>
                {messageToolbarMultipleSelection}
                <SmartDeleteDialog
                  show={openDeleteDialog}
                  cancelCallback={() => {
                    cancelOperation()
                    setOpenDeleteDialog(false)
                  }}
                  deleteCallBack={() => {
                    let deleteParam = datafetch
                    if (pageable) {
                      deleteParam = getPageableDeleteDialog()
                    }
                    deleteFunction(deleteParam)
                    setOpenDeleteDialog(false)
                  }}
                  rows={datafetch}
                  columns={columns}
                  pageable={getPageableDeleteDialog()}
                />
              </Typography>
              {filterText
                ? <Chip variant='outlined' label={filterText} />
                : null}
            </>
            )}
      </Toolbar>
    </>
  )
}
export default SmartMaterialUIToolbar
